'use-client';
import classNames from 'clsx';
import * as React from 'react';
import {
  getDataAttributes,
  sanitizeHTML,
} from '@wix/editor-elements-common-utils';
import type {
  ICustomElementComponentProps,
  ICustomElementComponentChildProps,
} from '../types';
import styles from './CustomElementComponent.scss';
import type { MutableRefObject } from 'react';

export type CustomHTMLElement = HTMLElement & {
  accessTokenListener?(accessTokenGetter: () => Promise<string>): unknown;
  [directPropKey: string]: any;
};

const CustomElementComponent: React.FC<
  ICustomElementComponentProps
> = props => {
  const {
    id,
    tagName,
    initialAttributes,
    attributes = {},
    seoMarkup,
    isInSeo = false,
    getAppToken,
    isEnabled = true,
    events = [],
    handleEvents,
    className,
    directMethods = {},
    directProps = {},
    onMouseEnter = () => {},
    onMouseLeave = () => {},
  } = props;
  const compRef = React.useRef<CustomHTMLElement>();
  const registeredEvents = React.useRef<Record<string, boolean>>({});
  const compAttributes = React.useMemo(() => {
    try {
      return JSON.parse(initialAttributes);
    } catch (e) {
      return {};
    }
  }, [initialAttributes]);
  const childProps: ICustomElementComponentChildProps = {
    ...compAttributes,
    ...attributes,
    ref: compRef,
    suppressHydrationWarning: true,
    dangerouslySetInnerHTML: { __html: '' }, // in order for hydration not to override inner html
  };

  if (isInSeo) {
    childProps.dangerouslySetInnerHTML = { __html: sanitizeHTML(seoMarkup) };
  }

  React.useEffect(() => {
    assignDirectProps({ compRef, directProps });
    assignDirectMethods({ compRef, directMethods });
  }, [compRef, directProps, directMethods]);

  React.useEffect(() => {
    if (!handleEvents) {
      return;
    }

    events
      .filter(eventName => !registeredEvents.current[eventName])
      .forEach(eventName => {
        compRef.current?.addEventListener(eventName, (e: any) => {
          handleEvents({ type: eventName, detail: e.detail });
        });
        registeredEvents.current[eventName] = true;
      });
  }, [events, handleEvents, registeredEvents]);
  React.useEffect(() => {
    if (getAppToken) {
      customElements
        .whenDefined(tagName)
        .then(() => compRef.current?.accessTokenListener?.(getAppToken))
        .catch(() => {});
    }
  }, [getAppToken, tagName]);

  if (!isEnabled) {
    return (
      <div
        id={id}
        wix-disabled-custom-element-reason="required-wix-premium-account"
      />
    );
  }

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={classNames(styles.customElementContainer, className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid="custom-element"
    >
      {React.createElement(tagName, childProps)}
    </div>
  );
};

export default CustomElementComponent;

function isRefPresent(
  compRef: MutableRefObject<CustomHTMLElement | undefined>,
): compRef is MutableRefObject<CustomHTMLElement> {
  return typeof compRef?.current === 'object' && compRef?.current !== null;
}

function assignDirectProps({
  compRef,
  directProps,
}: {
  compRef: MutableRefObject<CustomHTMLElement | undefined>;
  directProps: ICustomElementComponentProps['directProps'];
}) {
  if (!isRefPresent(compRef)) {
    return;
  }
  Object.entries(directProps).forEach(([setterName, setterValue]) => {
    compRef.current[setterName] = setterValue;
  });
}

function assignDirectMethods({
  compRef,
  directMethods,
}: {
  compRef: MutableRefObject<CustomHTMLElement | undefined>;
  directMethods: ICustomElementComponentProps['directMethods'];
}) {
  if (!isRefPresent(compRef)) {
    return;
  }
  Object.entries(directMethods).forEach(([setterName, setterEventId]) => {
    const setter = function (...args: any) {
      compRef.current.dispatchEvent(
        new CustomEvent(`${setterName}__${setterEventId}`, {
          detail: { args },
          bubbles: true,
        }),
      );
    };
    if (!isRefPresent(compRef)) {
      return;
    }
    compRef.current[setterName] = setter.bind(compRef.current);
  });
}
